import { Component, OnInit, EventEmitter, Output, Input, OnDestroy, OnChanges } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { SharedDataService } from '@app/services/net-remit/shared-data.service';
import { SharedService } from '@app/services/net-remit/shared-functions.service';

import { MessageService as PrimeMessageService } from 'primeng/api';
import { PrimeNG } from "primeng/config";

import { FilterViewComplete, FilterView } from '@app/shared/models/ipm/FilterView';
import { CommonFilterComboView } from '@app/shared/models/ipm/view/CommonFilterComboView';
import { CurrencyView } from '@app/shared/models/ipm/view/CurrencyView';
import { FilterIPMSelectionView } from '@app/shared/models/ipm/view/FilterIPMSelectionView';
import { RangeCalendarAvailableView } from '@app/shared/models/ipm/view/RangeCalendarAvailableView';
import { KpiData } from '@app/shared/models/ipm/KpiData';
import { BarChartData } from '@app/shared/models/ipm/BarChartData';
import { DocumentTypesView } from '@app/shared/models/ipm/view/DocumentTypesView';
import { CountryView } from '@app/shared/models/ipm/view/CountryView';
import { AirlineView } from '@app/shared/models/ipm/view/AirlineView';

import { default as constantsFile } from '@app/shared/constants.json';
import { finalize } from 'rxjs/internal/operators/finalize';
import { takeWhile } from 'rxjs/operators';

@Component({
  selector: 'app-charts-filter',
  templateUrl: './charts-filter.component.html',
  styleUrls: ['./charts-filter.component.scss'],
  standalone: false
})

export class ChartsFilterComponent implements OnInit, OnDestroy, OnChanges {

  anythingSelected: boolean;
  chartsFilter: string = 'chartsFilter';
  constants: any = constantsFile.constants;
  currencyAux: any;
  dateRange: Date[] = [];
  documentTypesPrevious: Array<CommonFilterComboView> = new Array<CommonFilterComboView>();
  firstLoad: boolean = true;
  isDateSelected: boolean = true;
  isLoadingComboData: boolean;
  isNotConvertible: boolean;
  filtersByDefault: FilterViewComplete;
  filterToShow: string[] = [];
  firstDayWeek: number;
  maxDate: Date;
  minDate: Date;
  validDateRange: boolean = true;
  withinRangeValue: boolean;

  formattedAirlinesSelected: string;
  formattedDocumentTypesSelected: string;
  formattedAgentsGroupsSelected: string;
  formattedAgentsSelected: string;
  formattedContractsSelected: string;
  formattedCountriesSelected: string;

  airlines: Array<CommonFilterComboView>      = new Array<CommonFilterComboView>();
  documentTypes: Array<CommonFilterComboView> = new Array<CommonFilterComboView>();
  countries: Array<CommonFilterComboView>     = new Array<CommonFilterComboView>();
  currencies: Array<CurrencyView>             = new Array<CurrencyView>();
  agents: Array<CommonFilterComboView>        = new Array<CommonFilterComboView>();
  agentsGroups: Array<CommonFilterComboView>  = new Array<CommonFilterComboView>();
  contracts: Array<CommonFilterComboView>     = new Array<CommonFilterComboView>();

  private alive: boolean = true;
  private allDocumentsStr: string;
  private firstSearch: boolean = true;
  private documentTypesAdap: Array<string> = new Array<string>();
  private createDataCombos(): FilterIPMSelectionView { return new FilterIPMSelectionView( new RangeCalendarAvailableView(null, null), [], [], []); }
  private dataCombos: FilterIPMSelectionView;

  public airlinesSelectedStr:       string = this.constants.commonFilterSelected.airlinesSelected;
  public countriesSelectedStr:      string = this.constants.commonFilterSelected.countriesSelected;
  public documentTypesSelectedStr:  string = this.constants.commonFilterSelected.documentTypesSelected;

  @Input() barCharts: BarChartData;
  @Input() filtersSelected: FilterViewComplete;
  @Input() isLoading: boolean;
  @Input() kpis: Array<KpiData>;
  @Input() maxMonthsAllowed: number;
  @Input() maxMonthsAllowedWithData: number;
  @Input() emptyCurrency: CurrencyView;

  @Output() sendFilters = new EventEmitter();
  @Output() stopLoading = new EventEmitter<{ hasError: boolean, message: string }>();

  constructor(
    private sharedDataService: SharedDataService,
    private sharedService: SharedService,
    private translate: TranslateService,
    private primeMessageService: PrimeMessageService,
    private primeConfig: PrimeNG
  ) {
    this.translate.get('primeng').subscribe((res) => {
      this.primeConfig.setTranslation(res);
    });
  }

  ngOnInit() {
    this.dataCombos = this.createDataCombos();
    this.filterToShow = [this.chartsFilter];
    this.buildDate([this.filtersSelected.fromDate, this.filtersSelected.toDate], false);
    this.documentTypesPrevious = this.filtersSelected.documentTypes;
    this.firstDayWeek = this.translate.currentLang == 'en-US' ? 0 : 1;
  }

  ngOnChanges() {
    if(this.kpis?.length > 0 && !this.isLoading){
      this.filterToShow = [];
      if(this.firstSearch){
        this.filterToShow = [this.chartsFilter];
        this.firstSearch = false;
      }
    } else {
      this.filterToShow = [this.chartsFilter];
    }
  }

  private syncRangeSelected(dataCombos: FilterIPMSelectionView): void {
    const from = this.filtersSelected?.fromDate;
    const to = this.filtersSelected?.toDate ?? from;

    if (!from) {
      return;
    }

    dataCombos.rangeSelected = new RangeCalendarAvailableView(
      new Date(Date.UTC(from.getFullYear(), from.getMonth(), from.getDate())),
      new Date(Date.UTC(to.getFullYear(), to.getMonth(), to.getDate()))
    );
  }

  public buildDate([fromDate, toDate]: any, isUpdatingCombos) {
    this.onSelectedDateRange(fromDate, toDate);
    this.syncRangeSelected(this.dataCombos);
    if(this.validDateRange && (this.filtersSelected.countries.length > 0 || this.firstSearch)  || !this.anythingSelected && this.withinRangeValue){
      this.getDataCombos(this.dataCombos, isUpdatingCombos);
    }
  }

  private onSelectedDateRange(fromDate, toDate){
    this.checkValidDateRange(fromDate, toDate, this.maxMonthsAllowedWithData);
    this.withinRangeValue = this.sharedService.withinRange(fromDate, toDate, this.maxMonthsAllowedWithData);
    if(this.withinRangeValue){
      this.checkAgentsGroupsContracts();
    }
    if(!this.firstSearch){ // It comes as undefined at the beggining. After getting the info for combos is set and therefore it is checked
      this.checkRangeDateSelected(fromDate, toDate);
    }
    this.filtersSelected.fromDate = fromDate;
    this.filtersSelected.toDate = toDate != null ? toDate : fromDate;
  }

  public buildDataCombo(dataCombo: any, property){
    this.dataCombos[property] = dataCombo?.map((data: CountryView | AirlineView | DocumentTypesView) => {
      switch(property){
        case this.countriesSelectedStr:
          return new CountryView(data['code'], data['name'], null, data['id']);
        case this.airlinesSelectedStr:
          return new AirlineView(data['id'], data['code'], data['name'], null);
        case this.documentTypesSelectedStr:
          return new DocumentTypesView(data['id'], data['code'], data['name']);
      }
    });
    if (this.validDateRange || this.withinRangeValue) {
      this.getDataCombos(this.dataCombos, true);
    }
  }

  public getDataCombos(dataCombos: FilterIPMSelectionView, isUpdatingCombos) {
    this.isLoadingComboData = isUpdatingCombos;
    this.syncRangeSelected(dataCombos);
    this.cleanCombos();
    this.sharedDataService.getDataCommonFilter(dataCombos)
      .pipe(finalize(() => { this.isLoadingComboData = false }), takeWhile(() => this.alive))
      .subscribe({ next: (combos) => {
        this.allDocumentsStr = this.translate.instant('All Documents');
        combos.documentTypesAvailable.unshift(new CommonFilterComboView(0, this.allDocumentsStr, ''));
        this.countries     = combos.countriesAvailable;
        this.airlines      = combos.airlinesAvailable;
        this.documentTypes = combos.documentTypesAvailable;
        this.agents        = combos.agentsAvailable;
        this.agentsGroups  = combos.agentGroupsAvailable;
        this.contracts     = combos.contractsAvailable;

        const minD     = new Date(combos.rangeCalendarAvailable.minDate);
        const maxD     = new Date(combos.rangeCalendarAvailable.maxDate);
        this.minDate       = new Date(Date.UTC(minD.getFullYear(), minD.getMonth(), minD.getDate()));
        this.maxDate       = new Date(Date.UTC(maxD.getFullYear(), maxD.getMonth(), maxD.getDate()));

        if(this.firstLoad){
          this.setDateRange(new Date(maxD.getFullYear(), maxD.getMonth() -1, maxD.getDate()), maxD);
          this.filtersSelected.fromDate = new Date(maxD.getFullYear(), maxD.getMonth() -1, maxD.getDate());
          this.filtersSelected.toDate = new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate());

          this.filtersSelected.documentTypes = [new CommonFilterComboView(0, this.allDocumentsStr, '')];
          this.filtersByDefault = { ...this.filtersSelected };
          this.firstLoad = false;
        }
        this.currencies = combos.currenciesAvailable.allCurrencies;
        this.formatFilter('documentTypes', 'formattedDocumentTypesSelected', 'code', 24);
        this.filtersSelected.currency = this.filtersByDefault.currency = combos.currenciesAvailable.userCurrency;

        if(!isUpdatingCombos){
          if (this.countries && this.countries.length > 0) {
            this.applyFilter();
          } else {
            this.stopLoading.emit({ hasError: true, message: 'No Countries' });
          }
        } else {
          this.stopLoading.emit({ hasError: false, message: '' });
        }
      }, error: (error) => {
        this.stopLoading.emit({ hasError: true, message: error && error.error ? error.error : 'Error Generic Onload' });
      }});
  }

  private setDateRange(dateFrom: Date, dateTo: Date){
    this.dateRange = [dateFrom, dateTo];
  }

  public setDateTo(){
    if(!this.dateRange[1]){
      this.dateRange[1] = this.dateRange[0];
    }
  }

  private cleanCombos(){
    //Limpia seleccion y datos de los combos dependientes
    this.agents = this.agentsGroups = this.contracts = [];
    this.filtersSelected.agents = [];
    this.filtersSelected.agentsGroups = [];
    this.filtersSelected.contracts = [];
    this.formatFilter('agents', 'formattedAgentsSelected', 'id', 28);
    this.formatFilter('agentsGroups', 'formattedAgentsGroupsSelected', 'id', 33);
    this.formatFilter('contracts', 'formattedContractsSelected', 'id', 33);
  }

  public clearSelection() {
    this.filtersSelected = { ...this.filtersByDefault };
    this.dataCombos = this.createDataCombos();
    this.buildDate([this.filtersByDefault.fromDate, this.filtersByDefault.toDate], true);
    this.formatFilter('countries', 'formattedCountriesSelected', 'id', 21);
    this.formatFilter('airlines', 'formattedAirlinesSelected', 'code', 12);

    this.filtersSelected.documentTypes = [ new CommonFilterComboView(0, this.allDocumentsStr, '') ];
    this.formatFilter('documentTypes', 'formattedDocumentTypesSelected', 'code', 24);

    this.formatFilter('agents', 'formattedAgentsSelected', 'id', 28);
    this.formatFilter('agentsGroups', 'formattedAgentsGroupsSelected', 'id', 33);
    this.formatFilter('contracts', 'formattedContractsSelected', 'id', 33);
    this.isNotConvertible = false;
  }

  public applyFilter() {
    this.adaptFilter();
    this.sendFilters.emit(
      new FilterView(
        new Date(Date.UTC(this.filtersSelected.fromDate.getFullYear(), this.filtersSelected.fromDate.getMonth(), this.filtersSelected.fromDate.getDate())).toDateString(),
        new Date(Date.UTC(this.filtersSelected.toDate.getFullYear(), this.filtersSelected.toDate.getMonth(), this.filtersSelected.toDate.getDate())).toDateString(),
        this.documentTypesAdap,
        this.filtersSelected.airlines.map(a => a.code),
        this.filtersSelected.countries.map(c => c.id),
        this.filtersSelected.currency.id,
        this.filtersSelected.currency.code,
        this.filtersSelected.agents.map(a => a.id),
        this.filtersSelected.agentsGroups.map(ag => ag.id),
        this.filtersSelected.contracts.map(c => c.id),
        this.filtersSelected.overCommission
      )
    );
  }

  public checkRangeDateSelected(fromDate, toDate) {
    this.isDateSelected = fromDate != null;
    // Se envían los maximos en graficos y reports (este ultimo sin seleccion de agentes, grupos o contratos)
    this.validDateRange = this.checkValidDateRange(fromDate, toDate, this.maxMonthsAllowed);

    if(this.isDateSelected && !this.validDateRange && !this.anythingSelected && this.withinRangeValue){
      this.primeMessageService.add({
        sticky: true,
        severity:'warn',
        summary: this.translate.instant('Modal.Warning'),
        detail: this.translate.instant('Period Bigger Select Data')
      });
    }
  }

  private checkValidDateRange(fromDate, toDate, maxMonths): boolean {
    const isValidDifference = fromDate == null || this.sharedService.withinRange(fromDate, toDate, maxMonths);

    // Si es primera busqueda o diferencia valida
    return this.firstSearch || isValidDifference;
  }

  private adaptFilter() {
    if(this.filtersSelected.documentTypes.find(dt => dt.code == this.allDocumentsStr)){
      this.documentTypesAdap = [];
      this.documentTypes.forEach((docType, index) => { if(index != 0) this.documentTypesAdap.push(docType.code) })
    } else {
      this.documentTypesAdap = this.filtersSelected.documentTypes.map(d => d.code);
    }
    this.filtersSelected.currency = this.isNotConvertible ? this.emptyCurrency : this.filtersSelected.currency;
  }

  public formatConversion(data, selection){
    this.filtersSelected[selection] = [ ...data ];
    if(selection == 'conversion'){
      if(this.isNotConvertible){
        this.currencyAux = { ...this.filtersSelected.currency };
        this.filtersSelected.currency = this.emptyCurrency;
      } else {
        this.filtersSelected.currency = this.currencyAux;
      }
    }
  }

  public checkApplyFilters(){
    return this.isLoadingComboData || !this.isDateSelected || (!this.validDateRange) || (!this.filtersSelected.documentTypes || this.filtersSelected.documentTypes.length == 0)
  }

  public formatFilter(selection, selectionMessage, attr, limit){
  this.checkAgentsGroupsContracts();
  this[selectionMessage] = undefined;
  this.filtersSelected[selection]?.forEach((item, index) => {
    const currentData = this[selection].find(s => s[attr] == item[attr]);
    this[selectionMessage] = index === 0 && !this[selectionMessage] ? currentData?.code : this[selectionMessage] + ", " + currentData?.code;
  });

  this[selectionMessage] = this[selectionMessage] ? this[selectionMessage].length < limit ? this[selectionMessage] : this[selectionMessage].substring(0,limit) + '...' : "";
}

  private checkAgentsGroupsContracts() {
    this.anythingSelected = this.firstSearch || this.filtersSelected.agents.length != 0 || this.filtersSelected.agentsGroups.length != 0 || this.filtersSelected.contracts.length != 0;
  }

  public checkDocumentsSelected(){
    const isAdding = this.filtersSelected.documentTypes.length > this.documentTypesPrevious.length;
    const newDoc = isAdding ? this.filtersSelected.documentTypes[this.filtersSelected.documentTypes.length - 1] : undefined;
    this.documentTypesPrevious = this.filtersSelected.documentTypes;

    if( !this.filtersSelected.documentTypes.find(dt => dt == this.allDocumentsStr) && this.filtersSelected.documentTypes.length == (this.documentTypes.length - 1) || isAdding && newDoc.code == this.allDocumentsStr){
      // Si se seleccionan todos (menos el 0), se selecciona el 0 y se deseleccionan los demás o si está añadiendo y el nuevo es 'todos', deselecciono todos
      this.filtersSelected.documentTypes = [new CommonFilterComboView(0, this.allDocumentsStr, '')];
    } else if(this.filtersSelected.documentTypes.find(dt => dt.code == this.allDocumentsStr) && this.filtersSelected.documentTypes.length > 1){
      // Si no, si está seleccionado el 0 y se selecciona cualquier otros, se deselecciona el 'todos'
      this.filtersSelected.documentTypes.splice(0, 1);
    }
    this.formatFilter('documentTypes', 'formattedDocumentTypesSelected', 'code', 24);
    this.buildDataCombo(this.filtersSelected.documentTypes, this.documentTypesSelectedStr);
  }

  ngOnDestroy(): void {
		this.alive = false;
  }
}
